import React, { useState, useRef } from "react";
import { DocumentIcon, PlusIcon, SpinnerIcon } from "../icons";
import FileCategorySelect from "./FileCategorySelect";

interface FileUploadZoneProps {
  onUpload: (
    file: File,
    metadata: {
      displayName: string;
      description?: string;
      category?: string;
      tags?: string[];
    }
  ) => void;
}

const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const ALLOWED_EXTENSIONS = [".pdf", ".xls", ".xlsx", ".doc", ".docx"];
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

const FileUploadZone: React.FC<FileUploadZoneProps> = ({ onUpload }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showMetadataForm, setShowMetadataForm] = useState(false);
  const [metadata, setMetadata] = useState({
    displayName: "",
    description: "",
    category: "offer_attachment",
    tags: [] as string[],
  });
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `Plik jest za duży. Maksymalny rozmiar to 100MB.`;
    }

    // Check file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      const fileName = file.name.toLowerCase();
      const hasValidExtension = ALLOWED_EXTENSIONS.some((ext) =>
        fileName.endsWith(ext)
      );

      if (!hasValidExtension) {
        return `Nieobsługiwany format pliku. Dozwolone formaty: PDF, XLS, XLSX, DOC, DOCX`;
      }
    }

    return null;
  };

  const handleFileSelect = (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setSelectedFile(file);
    setMetadata((prev) => ({
      ...prev,
      displayName: file.name.replace(/\.[^/.]+$/, ""), // Remove extension
    }));
    setShowMetadataForm(true);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      await onUpload(selectedFile, metadata);

      // Reset form
      setSelectedFile(null);
      setShowMetadataForm(false);
      setMetadata({
        displayName: "",
        description: "",
        category: "offer_attachment",
        tags: [],
      });

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setShowMetadataForm(false);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  if (showMetadataForm && selectedFile) {
    return (
      <div className="border-2 border-gray-300 border-dashed rounded-lg p-6">
        <div className="text-center mb-4">
          <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Szczegóły pliku: {selectedFile.name}
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            Rozmiar: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nazwa wyświetlana *
            </label>
            <input
              type="text"
              value={metadata.displayName}
              onChange={(e) =>
                setMetadata((prev) => ({
                  ...prev,
                  displayName: e.target.value,
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Nazwa pliku widoczna w systemie"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Opis
            </label>
            <textarea
              value={metadata.description}
              onChange={(e) =>
                setMetadata((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={2}
              placeholder="Opcjonalny opis pliku"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kategoria
            </label>
            <FileCategorySelect
              value={metadata.category}
              onChange={(category: string) =>
                setMetadata((prev) => ({ ...prev, category }))
              }
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Anuluj
          </button>
          <button
            onClick={handleUpload}
            disabled={isUploading || !metadata.displayName.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-700 border border-transparent rounded-md hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isUploading && <SpinnerIcon className="h-4 w-4 animate-pulse" />}
            <span>{isUploading ? "Przesyłam..." : "Prześlij plik"}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
        isDragOver
          ? "border-primary-400 bg-primary-50"
          : "border-gray-300 hover:border-gray-400"
      }`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={() => fileInputRef.current?.click()}
    >
      <PlusIcon className="mx-auto h-12 w-12 text-gray-400" />
      <h3 className="mt-2 text-sm font-medium text-gray-900">
        Przeciągnij pliki tutaj lub kliknij aby wybrać
      </h3>
      <p className="mt-1 text-xs text-gray-500">
        Obsługiwane formaty: PDF, XLS, XLSX, DOC, DOCX
      </p>
      <p className="text-xs text-gray-500">Max. 100 MB</p>

      {error && (
        <div className="mt-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
          {error}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_EXTENSIONS.join(",")}
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  );
};

export default FileUploadZone;
